// client/src/components/HOME/HomeUI.js
// Briques de présentation partagées par les pages vitrine (/, /homeclient,
// /homecommercant, /homeaffiliates) : styles embarqués + petits composants.
// Uniquement de la mise en forme : aucune logique métier ici.

import React from "react";
import {
  FiSmartphone, FiMessageCircle, FiGift, FiFileText, FiHeart, FiStar,
  FiUsers, FiTrendingUp, FiBarChart2, FiTarget, FiCheck,
} from "react-icons/fi";
import { HiOutlineMegaphone } from "react-icons/hi2";

// Pictogrammes sobres à la place des emojis des cartes (même sens, rendu pro)
const ICONS = {
  "📱": FiSmartphone,
  "💬": FiMessageCircle,
  "🎁": FiGift,
  "🧾": FiFileText,
  "❤️": FiHeart,
  "⭐️": FiStar,
  "📣": HiOutlineMegaphone,
  "🤝": FiUsers,
  "💰": FiTrendingUp,
  "📊": FiBarChart2,
  "🎯": FiTarget,
};

export const HOME_CSS = `
.lp-page {
  --lp-ink: var(--eb-forest);
  --lp-ink-2: rgba(20, 48, 40, 0.74);
  --lp-ink-3: rgba(20, 48, 40, 0.56);
  --lp-line: rgba(20, 48, 40, 0.11);
  --lp-tint: rgba(20, 48, 40, 0.06);
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  color: var(--lp-ink);
  background:
    radial-gradient(900px 420px at 12% 0%, rgba(20, 48, 40, 0.08), transparent 60%),
    radial-gradient(900px 420px at 88% 8%, rgba(39, 76, 116, 0.07), transparent 60%),
    linear-gradient(180deg, var(--eb-cream) 0%, #fffdf8 45%, #ffffff 100%);
}
.lp-main { flex: 1; padding-bottom: 72px; }
.lp-shell { width: 100%; max-width: 1180px; margin: 0 auto; padding: 0 clamp(16px, 5vw, 32px); }

/* Hero */
.lp-hero { padding: 64px 0 8px; }
.lp-hero__grid { display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr); gap: 56px; align-items: center; }
.lp-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 14px 6px 10px; border-radius: var(--eb-radius-pill);
  border: 1px solid var(--lp-line); background: rgba(255, 255, 255, 0.75);
  font-size: 13px; font-weight: 600; color: var(--lp-ink); letter-spacing: 0.01em;
}
.lp-eyebrow__dot { width: 8px; height: 8px; border-radius: 50%; background: var(--lp-ink); box-shadow: 0 0 0 4px var(--lp-tint); }
.lp-h1, .lp-h2, .lp-h3 { text-align: inherit; }
.lp-h1 {
  margin: 20px 0 16px; font-family: var(--eb-font-serif); font-weight: 700; color: var(--lp-ink);
  font-size: clamp(2.1rem, 4.2vw, 3.3rem); line-height: 1.06; letter-spacing: -0.025em;
}
.lp-lead { margin: 0; max-width: 540px; font-size: 1.125rem; line-height: 1.6; color: var(--lp-ink-2); }
.lp-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 28px; }
.lp-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 24px; }
.lp-chip {
  display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px;
  border-radius: var(--eb-radius-pill); border: 1px solid var(--lp-line);
  background: rgba(255, 255, 255, 0.7); font-size: 13px; font-weight: 600; color: var(--lp-ink-2);
}

/* Boutons vitrine */
.lp-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  min-height: 48px; padding: 0 22px; border-radius: var(--eb-radius-pill);
  border: 1px solid transparent; font-weight: 600; font-size: 15px; line-height: 1;
  text-decoration: none; white-space: nowrap; cursor: pointer;
  transition: transform 0.15s var(--eb-ease), box-shadow 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}
.lp-btn:hover { transform: translateY(-1px); }
.lp-btn:focus-visible { outline: 2px solid var(--eb-brand-500); outline-offset: 2px; }
.lp-btn--primary { background: var(--lp-ink); color: #fff; box-shadow: 0 10px 24px -10px rgba(20, 48, 40, 0.55); }
.lp-btn--primary:hover { color: #fff; box-shadow: 0 14px 28px -10px rgba(20, 48, 40, 0.6); }
.lp-btn--secondary { background: #fff; color: var(--lp-ink); border-color: rgba(20, 48, 40, 0.16); box-shadow: var(--eb-shadow-xs); }
.lp-btn--secondary:hover { color: var(--lp-ink); background: var(--eb-cream); }
.lp-btn__arrow { transition: transform 0.15s var(--eb-ease); }
.lp-btn:hover .lp-btn__arrow { transform: translateX(2px); }

/* Médias / cartes */
.lp-media {
  border-radius: 24px; overflow: hidden; background: #fff;
  border: 1px solid var(--lp-line);
  box-shadow: 0 1px 2px rgba(20, 48, 40, 0.04), 0 28px 60px -24px rgba(20, 48, 40, 0.28);
}
.lp-media img { display: block; width: 100%; height: 340px; object-fit: cover; }
.lp-media--wide img { height: 300px; }
.lp-card {
  background: rgba(255, 255, 255, 0.86); border: 1px solid var(--lp-line); border-radius: 20px;
  box-shadow: 0 1px 2px rgba(20, 48, 40, 0.04), 0 16px 40px -24px rgba(20, 48, 40, 0.22);
  padding: 28px;
  text-align: left;
}

/* Sections */
.lp-section { padding-top: 72px; }
.lp-section--tight { padding-top: 40px; }
.lp-section-head { max-width: 640px; margin: 0 auto 32px; text-align: center; }
.lp-kicker { display: block; margin-bottom: 10px; font-size: 12.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--lp-ink-3); }
.lp-h2 { margin: 0; font-family: var(--eb-font-serif); font-weight: 700; color: var(--lp-ink); font-size: clamp(1.65rem, 3vw, 2.2rem); line-height: 1.12; letter-spacing: -0.02em; }
.lp-h3 { margin: 0; font-family: var(--eb-font-serif); font-weight: 700; color: var(--lp-ink); font-size: 1.3rem; line-height: 1.2; letter-spacing: -0.01em; }
.lp-text { margin: 0; color: var(--lp-ink-2); line-height: 1.6; font-size: 1rem; }

.lp-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr)); gap: 20px; }
.lp-feature { display: flex; flex-direction: column; gap: 14px; height: 100%; }
.lp-feature__head { display: flex; align-items: center; gap: 14px; }
.lp-icon {
  flex-shrink: 0; width: 48px; height: 48px; border-radius: 14px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--lp-tint); border: 1px solid var(--lp-line); color: var(--lp-ink); font-size: 22px;
}
.lp-note {
  margin-top: 4px; padding: 14px 16px; border-radius: var(--eb-radius);
  background: var(--eb-cream); border: 1px solid var(--lp-line);
  color: var(--lp-ink); font-size: 14.5px; line-height: 1.55; font-weight: 500;
}

/* Listes à puces cochées */
.lp-checks { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.lp-check { display: flex; align-items: flex-start; gap: 10px; color: var(--lp-ink-2); line-height: 1.5; font-size: 15.5px; }
.lp-check__mark {
  flex-shrink: 0; margin-top: 2px; width: 20px; height: 20px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--lp-ink); color: #fff; font-size: 12px;
}

/* Bandeau "split" (image + texte) */
.lp-split { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 56px; align-items: center; padding: 40px 48px; }
.lp-split--reverse .lp-split__media { order: 2; }
.lp-split__media {
  border-radius: 20px; background: var(--eb-cream); border: 1px solid var(--lp-line);
  aspect-ratio: 4 / 3; max-width: 400px; width: 100%; justify-self: center;
  display: flex; align-items: center; justify-content: center; overflow: hidden;
}
.lp-split__media img { width: 100%; height: 100%; object-fit: contain; padding: 20px; }
.lp-steps { margin: 16px 0 24px; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 12px; }
.lp-steps li { display: flex; gap: 12px; align-items: flex-start; color: var(--lp-ink-2); line-height: 1.5; }
.lp-steps__num {
  flex-shrink: 0; width: 26px; height: 26px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid rgba(20, 48, 40, 0.2); color: var(--lp-ink); font-size: 13px; font-weight: 700; background: #fff;
}

/* Bandeau sombre (appel à l'action) */
.lp-band {
  margin-top: 24px; padding: 24px 28px; border-radius: 20px;
  background: var(--lp-ink); color: #fff;
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  box-shadow: 0 24px 48px -28px rgba(20, 48, 40, 0.7);
}
.lp-band__title { font-family: var(--eb-font-serif); font-weight: 700; font-size: 1.3rem; letter-spacing: -0.01em; }
.lp-band__text { margin-top: 4px; color: rgba(255, 255, 255, 0.72); font-size: 14.5px; font-weight: 500; }

/* Défilement des logos clients */
@keyframes ecobill-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
.lp-logos {
  overflow: hidden; padding: 14px 0; border-radius: 20px;
  border: 1px solid var(--lp-line); background: rgba(255, 255, 255, 0.75);
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent);
  mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent);
}
.ecobill-marquee { display: inline-flex; min-width: max-content; animation: ecobill-marquee 36s linear infinite; will-change: transform; }
.ecobill-marquee:hover { animation-play-state: paused; }
.lp-logo { width: 200px; min-width: 200px; height: 56px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; padding: 0 18px; }
.lp-logo img { max-width: 100%; max-height: 100%; object-fit: contain; }
.lp-proof { margin: 0 0 16px; text-align: center; color: var(--lp-ink-2); font-weight: 500; font-size: 15px; }
.lp-proof strong { color: var(--lp-ink); font-weight: 700; }

@media (max-width: 899px) {
  .lp-hero { padding: 32px 0 0; text-align: center; }
  .lp-hero__grid { grid-template-columns: 1fr; gap: 32px; }
  .lp-lead { margin: 0 auto; font-size: 1.03rem; }
  .lp-actions, .lp-chips { justify-content: center; }
  .lp-media img { height: 230px; }
  .lp-section { padding-top: 48px; }
  .lp-section-head { margin-bottom: 24px; }
  .lp-card { padding: 22px 20px; }
  .lp-split { grid-template-columns: 1fr; gap: 24px; padding: 24px 20px 28px; text-align: center; }
  .lp-split--reverse .lp-split__media { order: 0; }
  .lp-split__media { max-width: 240px; }
  .lp-steps { text-align: left; max-width: 420px; margin-left: auto; margin-right: auto; }
  .lp-band { padding: 22px 20px; }
  .lp-main { padding-bottom: 48px; }
}
@media (max-width: 420px) {
  .lp-actions .lp-btn { flex: 1 1 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .ecobill-marquee { animation: none; }
}
`;

export function HomeStyles() {
  return <style>{HOME_CSS}</style>;
}

export function StepLine({ children }) {
  return (
    <li className="lp-check">
      <span className="lp-check__mark" aria-hidden="true"><FiCheck /></span>
      <span>{children}</span>
    </li>
  );
}

/** Carte de fonctionnalité des pages vitrine (pictogramme + titre + contenu). */
export function FeatureCard({ emoji, title, children }) {
  const Icon = ICONS[emoji];
  return (
    <section className="lp-card lp-feature">
      <div className="lp-feature__head">
        <span className="lp-icon" aria-hidden="true">{Icon ? <Icon /> : emoji}</span>
        <h2 className="lp-h3">{title}</h2>
      </div>
      <div>{children}</div>
    </section>
  );
}
